import React, { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'
import {
  ArrowLeft, CalendarDays, ChevronDown, ChevronRight, CircleUserRound,
  CheckCircle2, Clock3, CreditCard, Gift, House, LockKeyhole, MapPin, MessageCircle,
  Pencil, Send, Sparkles, Trash2, X, WalletCards
} from 'lucide-react'
import './styles.css'
import webApp, { openTelegramLink, showAlert } from './tg.js'
import {
  ACCESS_CODES, accessCopy, ApiError, BLOCKED_EVENT, clearAiHistory, drawDailyMessage,
  fetchAstroHistory, fetchAstroReadings, fetchSavedTarotReadings, fetchTarotSpheres,
  fetchTarotSpreads, generateAstroReading, generateTarotReading, getAccess, getAiHistory, getAiSession,
  getDailyMessage, getReferrals, getTrialTariff, isAccessError, isBlockedError, registerUser,
  selectAstroReading, selectTarotSpread, sendAiChat, startCardsChat, startDreamChat
} from './api.js'
import Markdown from './components/Markdown.jsx'
import TariffsScreen from './components/Tariffs.jsx'
import HistoryScreen, { SendPdfButton } from './components/History.jsx'
import { Glass, Orb, Pill, SparkLine } from './components/ui.jsx'
// Список городов и матчер подсказок — из шаблонного CityField (сам компонент не используется)
import { suggest as suggestCities } from './components/CityField.jsx'
import { cardFace } from './cardFace.js'
// Админка грузится отдельным чанком: обычному пользователю она не нужна,
// а её формы и стили заметно тяжелее остальных экранов.
const AdminPanel=lazy(()=>import('./admin/AdminPanel.jsx'))

const A='/assets/'
const screens={HOME:'home',CATEGORIES:'categories',LIST:'list',READING:'reading',ASSISTANT:'assistant',BOT:'bot',CHAT:'chat',DREAM:'dream',ASTRO:'astro',BIRTH:'birth',FORMULA:'formula',ARCHIVE:'archive',TARIFFS:'tariffs',PROFILE:'profile',ADMIN:'admin'}
const plural=(n,one,few,many)=>{const m10=n%10,m100=n%100;if(m10===1&&m100!==11)return one;if(m10>=2&&m10<=4&&(m100<12||m100>14))return few;return many}
const nReadings=n=>`${n} ${plural(n,'разбор','разбора','разборов')}`
const nSpreads=n=>`${n} ${plural(n,'расклад','расклада','раскладов')}`
const nQuestions=n=>`${n} ${plural(n,'вопрос','вопроса','вопросов')}`
const nPoints=n=>`${n} ${plural(n,'пункт','пункта','пунктов')} разбора`
const GUIDED_DRAFT_KEY='clavisGuidedDraft'
const GUIDED_NAME_KEY='clavisGuidedName'
const DREAM_DRAFT_KEY='clavisDreamDraft'
let guidedChatBootstrap=null
let dreamChatBootstrap=null

function claimDreamChat(){
  if(dreamChatBootstrap)return dreamChatBootstrap
  const dream=sessionStorage.getItem(DREAM_DRAFT_KEY)
  if(!dream?.trim())return null
  sessionStorage.removeItem(DREAM_DRAFT_KEY)
  const promise=startDreamChat().then(selection=>sendAiChat(dream.trim()).then(response=>({selection,response})))
  dreamChatBootstrap={draft:{question:dream.trim()},promise}
  promise.finally(()=>setTimeout(()=>{dreamChatBootstrap=null},0)).catch(()=>{})
  return dreamChatBootstrap
}

function claimGuidedChat(){
  if(guidedChatBootstrap)return guidedChatBootstrap
  const raw=sessionStorage.getItem(GUIDED_DRAFT_KEY)
  if(!raw)return null
  try{
    const draft=JSON.parse(raw)
    if(!draft?.name?.trim()||!draft?.topic?.trim()||!draft?.question?.trim())return null
    sessionStorage.removeItem(GUIDED_DRAFT_KEY)
    localStorage.setItem(GUIDED_NAME_KEY,draft.name.trim())
    // Личный вопрос идёт в режим одной карты без выбора каталожного расклада.
    const prompt=`Меня зовут ${draft.name.trim()}. Моя тема — ${draft.topic.trim()}.\n\n${draft.question.trim()}`
    const promise=startCardsChat(draft.topic.trim())
      .then(selection=>sendAiChat(prompt).then(response=>({selection,response})))
    guidedChatBootstrap={draft,promise}
    promise.then(
      ()=>setTimeout(()=>{guidedChatBootstrap=null},0),
      ()=>setTimeout(()=>{guidedChatBootstrap=null},0),
    )
    return guidedChatBootstrap
  }catch{
    sessionStorage.removeItem(GUIDED_DRAFT_KEY)
    return null
  }
}

// Glass / Orb / Pill / SparkLine живут в components/ui.jsx — тем же набором
// пользуется экран тарифов, чтобы не расходиться по оформлению (money нужен только
// там: цены показывает витрина, а доступ описывается покупками, а не рублями).
// Аватар из Telegram (initDataUnsafe.user.photo_url). Буква имени — не заглушка на
// секунду, а постоянный низ слоя: фото проявляется поверх только по onLoad, поэтому
// пустой photo_url (закрытый профиль), медленная сеть и битая ссылка выглядят одинаково.
function Avatar({name,photo}){
  const [ready,setReady]=useState(false)
  useEffect(()=>{setReady(false)},[photo])
  return <div className="avatar">
    <span>{name?.[0]||'?'}</span>
    {photo&&<img className={ready?'is-ready':''} src={photo} alt="" referrerPolicy="no-referrer" onLoad={()=>setReady(true)} onError={()=>setReady(false)}/>}
  </div>
}
function AiModal({title,subtitle,children,onClose,loading=false,kicker='Разбор'}){
  useEffect(()=>{const close=e=>{if(e.key==='Escape')onClose?.()};addEventListener('keydown',close);return()=>removeEventListener('keydown',close)},[onClose])
  return <div className="ai-modal-backdrop" role="presentation" onMouseDown={e=>{if(e.target===e.currentTarget&&!loading)onClose?.()}}>
    <section className="ai-modal" role="dialog" aria-modal="true" aria-label={title}>
      {!loading&&<button className="ai-modal-close press" onClick={onClose} aria-label="Закрыть"><X/></button>}
      {loading?<div className="ai-loading"><Orb/><b>{title}</b><p>{subtitle}</p><span/></div>:<><span className="ai-modal-kicker">{kicker}</span><h3>{title}</h3>{subtitle&&<p className="ai-modal-subtitle">{subtitle}</p>}<div className="ai-modal-copy">{children}</div></>}
    </section>
  </div>
}

// Экран «это надо купить». Один на все четыре причины отказа (402): нет подписки
// Таро, нет астро-пакета, кончились разборы в пакете, кончились вопросы к ИИ —
// текст берётся по коду ошибки из accessCopy(). Денег здесь не списывается: реальной
// оплаты пока нет, поэтому единственное действие — уйти на витрину тарифов.
function Paywall({error,onClose,onBuy}){
  const {title,text}=accessCopy(error)
  const balance=error?.body?.balance
  return <div className="ai-modal-backdrop commerce-backdrop" role="presentation" onMouseDown={e=>{if(e.target===e.currentTarget)onClose?.()}}>
    <section className="commerce-modal" role="dialog" aria-modal="true" aria-label={title}>
      <button className="ai-modal-close press" onClick={onClose} aria-label="Закрыть"><X/></button>
      <span className="ai-modal-kicker">Доступ</span>
      <LockKeyhole className="commerce-icon"/>
      <h3>{title}</h3>
      <p className="commerce-copy">{text}</p>
      {balance&&<div className="commerce-balance"><span>Вопросов к ИИ осталось</span><b>{balance.total}</b></div>}
      <Pill className="commerce-primary" onClick={onBuy}><CreditCard size={16}/> Смотреть тарифы</Pill>
    </section>
  </div>
}

// Всплывашка «у вас есть бесплатный тариф». Показывается на запуске приложения, пока
// подарок не забран.
//
// Показывать или нет — решает бэк (GET /tariffs/trial), а не localStorage: тариф
// выключается чекбоксом в админке, и запомненное «уже показывали» пережило бы и
// выключение, и включение обратно. Закрыть её насовсем поэтому нельзя — «Позже»
// закрывает окно до следующего запуска. Исчезает она в двух случаях: подарок забрали
// (activated) или его сняли с витрины (available=false).
//
// Состав берётся из тарифа, а не из захардкоженного текста, — иначе правка «1 расклад»
// на «2 расклада» в админке разошлась бы с тем, что обещает окно.
//
// Свёрстано по образцу Paywall (commerce-modal), а не AiModal: у того содержимое
// лежит в рамке ai-modal-copy, рассчитанной на простыню текста разбора, и короткое
// предложение в ней выглядит коробкой в коробке. Действий два, но равными кнопками
// они и не должны быть — «Позже» это отказ, ему хватает ссылки под главной кнопкой.
function TrialOffer({trial,onClose,onGo}){
  const tariff=trial?.tariff
  const title=tariff?.title||'Бесплатный тариф'
  const spreads=tariff?.readings_quota||0
  const messages=tariff?.total_messages||0
  return <div className="ai-modal-backdrop commerce-backdrop" role="presentation" onMouseDown={e=>{if(e.target===e.currentTarget)onClose?.()}}>
    <section className="commerce-modal" role="dialog" aria-modal="true" aria-label={title}>
      <button className="ai-modal-close press" onClick={onClose} aria-label="Закрыть"><X/></button>
      <span className="ai-modal-kicker">Подарок</span>
      <Gift className="commerce-icon"/>
      <h3>{title}</h3>
      {/* Короткая строка вместо описания: подробности несёт список ниже, а полное
          описание из каталога и так стоит на карточке тарифа. */}
      <p className="commerce-copy">{tariff?.subtitle||tariff?.description||'Доступен для активации'}</p>
      <ul className="trial-offer">
        {spreads>0&&<li>{nSpreads(spreads)} на выбор из всего каталога</li>}
        {messages>0&&<li>{nQuestions(messages)} к ИИ по нему</li>}
        {/* Условие называем сразу: наткнуться на него уже нажав «Забрать» — обидно.
            Строки нет, когда подписка не требуется (бэк не прислал канал). */}
        {trial?.channel?.title?<li>Нужна подписка на канал {trial.channel.title}</li>:<li>Без оплаты и без карты</li>}
      </ul>
      <Pill className="commerce-primary" onClick={onGo}><Gift size={16}/> Забрать в тарифах</Pill>
      <button className="commerce-link press" onClick={onClose}>Позже</button>
    </section>
  </div>
}

// Подпись «сколько запросов осталось» — одинаковая в чате и в профиле, поэтому
// собирается в одном месте. Бесплатные и платные показываются раздельно: это
// разные вещи для пользователя, хотя тратятся подряд.
function balanceLabel(balance){
  if(!balance)return ''
  const parts=[]
  if(balance.free)parts.push(`${balance.free} ${plural(balance.free,'бесплатный','бесплатных','бесплатных')}`)
  if(balance.paid)parts.push(`${balance.paid} ${plural(balance.paid,'платный','платных','платных')}`)
  if(!parts.length)return 'Вопросы к ИИ закончились'
  return `Осталось ${parts.join(' и ')} ${plural(balance.total,'вопрос','вопроса','вопросов')}`
}

// Откуда пришли оставшиеся вопросы: кошельки с бэка (balance.wallets) — это не разные
// лимиты, а происхождение сообщений, тратятся они одинаково на любой запрос к ИИ.
const WALLET_TITLE={tarot:'по подписке Таро',astrology:'по астро-пакету',shared:'докупленные'}
function walletsLabel(balance){
  const rows=(balance?.wallets||[]).filter(w=>w.total)
  // Один кошелёк ничего не добавляет к общему остатку — подпись только для нескольких.
  if(rows.length<2&&!rows.some(w=>w.expires_at))return ''
  return rows.map(w=>`${WALLET_TITLE[w.scope]||w.scope} — ${w.total}${w.expires_at?` (до ${new Date(`${w.expires_at}Z`).toLocaleDateString('ru-RU')})`:''}`).join(' · ')
}

// Как называется тема в шапке чата. Модуль приходит с бэка (GET /ai/session), а
// «свободный чат» — такая же тема, просто без расклада за спиной.
const CONTEXT_KIND={tarot:'Расклад Таро',astrology:'Астро-разбор',general:'Свободный чат',cards:'Вопрос картам',dream:'Сонник'}
// Названия покупок. Основное берётся из тарифа (`tariff_title` в GET /users/me/access),
// а kind — запасной вариант для доступа по позиции, которую убрали из каталога.
const KIND_TITLE={tarot_subscription:'Подписка на Таро',astro_package:'Астро-пакет',message_pack:'Пакет вопросов'}
const SOURCE_LABEL={gift:'подарок',trial:'пробный доступ',admin:'выдано вручную'}
const entitlementTitle=item=>item.tariff_title||KIND_TITLE[item.kind]||item.kind
// Срок считает сервер (истёкшее в /access просто не приходит) — здесь только формат.
// Даты приходят без зоны, но хранятся в UTC, поэтому час пояса дописываем сами:
// иначе у пользователя восточнее UTC подписка «кончалась» на день раньше.
function accessUntil(iso){
  if(!iso)return 'Бессрочно'
  const date=new Date(/(Z|[+-]\d\d:?\d\d)$/.test(iso)?iso:`${iso}Z`)
  return Number.isNaN(date.getTime())?'Бессрочно':`До ${date.toLocaleDateString('ru-RU',{day:'numeric',month:'long',year:'numeric'})}`
}
// Строка под названием покупки: срок, остаток штук и то, как доступ получен.
// Остаток называется по модулю покупки: колонка на бэке одна (`readings_left` считает
// и разборы, и расклады), а пробный тариф даёт именно расклады.
const nLeft=item=>item.scope==='tarot'?nSpreads(item.readings_left):nReadings(item.readings_left)
const noneLeft=item=>item.scope==='tarot'?'расклады закончились':'разборы закончились'
function entitlementNote(item){
  const parts=[accessUntil(item.expires_at)]
  if(item.readings_left!=null)parts.push(item.is_exhausted?noneLeft(item):`осталось ${nLeft(item)}`)
  if(SOURCE_LABEL[item.source])parts.push(SOURCE_LABEL[item.source])
  return parts.join(' · ')
}
// Что открыто по Таро, одной строкой. Подписка описывается сроком, пробный тариф —
// остатком раскладов: срока у него нет, и общее «Таро без срока» прочиталось бы как
// безлимит ровно там, где раскладов уже не осталось.
function tarotLabel(access){
  if(access.subscription_until)return `Таро ${accessUntil(access.subscription_until).toLowerCase()}`
  const rows=(access.entitlements||[]).filter(item=>item.scope==='tarot')
  if(rows.some(item=>item.readings_left==null))return 'Таро без срока'
  const left=rows.reduce((total,item)=>total+item.readings_left,0)
  return left?`Таро: ${nSpreads(left)}`:'Таро: бесплатный расклад использован'
}
// Сводка по покупкам одной строкой — то же, что и в списке, но свёрнутое бэком:
// subscription_until и readings_left уже сложены по всем живым доступам.
function accessLabel(access){
  if(!access)return ''
  if(!access.entitlements?.length)return 'Пока ничего не куплено'
  const parts=[]
  if(access.has_tarot)parts.push(tarotLabel(access))
  if(access.has_astrology)parts.push(access.readings_left==null?'Разборы без лимита':`${nReadings(access.readings_left)} в пакете`)
  return parts.join(' · ')
}

const MONTHS={января:'01',февраля:'02',марта:'03',апреля:'04',мая:'05',июня:'06',июля:'07',августа:'08',сентября:'09',октября:'10',ноября:'11',декабря:'12'}
function normalizeBirthDate(value){
  if(/^\d{4}-\d{2}-\d{2}$/.test(value||''))return value
  const match=String(value||'').trim().toLowerCase().match(/^(\d{1,2})\s+([а-яё]+)\s+(\d{4})$/)
  return match&&MONTHS[match[2]]?`${match[3]}-${MONTHS[match[2]]}-${String(match[1]).padStart(2,'0')}`:''
}
function formatBirthDate(value){
  const iso=normalizeBirthDate(value)
  if(!iso)return value
  const [year,month,day]=iso.split('-')
  const labels=['января','февраля','марта','апреля','мая','июня','июля','августа','сентября','октября','ноября','декабря']
  return `${Number(day)} ${labels[Number(month)-1]} ${year}`
}
// Без демо-заглушек: пока пользователь ничего не вводил, поля пустые,
// а не фейковые «12 мая 1995, Москва», выглядящие как настоящие данные.
function readStoredBirth(){
  try{
    const saved=JSON.parse(localStorage.getItem('birthData'))||{}
    return {date:normalizeBirthDate(saved.date)||'',time:saved.time||'',place:saved.place||''}
  }catch{return{date:'',time:'',place:''}}
}

function BottomNav({screen,go}){
  const homeActive=[screens.HOME,screens.CATEGORIES,screens.LIST,screens.READING,screens.ASTRO,screens.BIRTH,screens.FORMULA,screens.ARCHIVE,screens.TARIFFS].includes(screen)
  const chatActive=[screens.ASSISTANT,screens.BOT,screens.CHAT].includes(screen)
  return <nav className="bottom-nav">
    <button className={`press ${homeActive?'active':''}`} onClick={()=>go(screens.HOME)}><House/><span>Главная</span></button>
    <button className={`press ${chatActive?'active':''}`} onClick={()=>go(screens.CHAT)}><MessageCircle/><span>Clavis</span></button>
    <button className={`press ${screen===screens.PROFILE?'active':''}`} onClick={()=>go(screens.PROFILE)}><CircleUserRound/><span>Профиль</span></button>
  </nav>
}

function Topbar({go,back=screens.HOME,label='Отношения'}){return <header className="topbar"><button className="press" onClick={()=>go(back)}><ArrowLeft size={17}/> Назад</button><span><Orb/> {label}</span></header>}

// Сколько осталось до следующего послания. next_available_at приходит с бэка —
// это полночь UTC после дня выдачи, ровно та граница, по которой бэк и пускает
// за новым посланием, поэтому подпись на карте не расходится с реальным лимитом.
function untilLabel(iso,now){
  const left=new Date(iso).getTime()-now
  if(!(left>0))return ''
  const minutes=Math.ceil(left/60000)
  const hours=Math.floor(minutes/60),rest=minutes%60
  if(hours>=24)return 'завтра'
  if(!hours)return `через ${minutes} мин`
  return rest?`через ${hours} ч ${rest} мин`:`через ${hours} ч`
}

// Сферы таро — из GET /tarot/spheres, а не из хардкода: иначе сферы,
// добавленные в каталоге (например «Психология»), не видны в приложении.
// Кэш + фолбэк на базовую тройку, чтобы кнопки были и до первой загрузки.
const SPHERES_CACHE_KEY='tarotSpheres'
const SPHERE_FALLBACK=['Отношения','Работа','Финансы']
const SPHERE_SUBTITLES={'Отношения':'Любовь, партнёрство, семья и чувства','Работа':'Карьера, цели, предназначение','Финансы':'Деньги, изобилие, материальный поток','Психология':'Внутреннее состояние и ресурс'}
// «Я не знаю, что со мной» — пользовательское название сферы «Психология»:
// по задумке заказчицы эта плитка открывает психологические расклады каталога.
const PSY_SPHERE='Психология'
const PSY_TITLE='Я не знаю, что со мной'
const displaySphere=s=>s===PSY_SPHERE?PSY_TITLE:s
function readSpheresCache(){
  try{const c=JSON.parse(localStorage.getItem(SPHERES_CACHE_KEY));return Array.isArray(c)&&c.length?c:null}catch{return null}
}
function useTarotSpheres(){
  const [spheres,setSpheres]=useState(()=>readSpheresCache()||SPHERE_FALLBACK)
  useEffect(()=>{
    let live=true
    fetchTarotSpheres().then(list=>{
      if(!live||!Array.isArray(list)||!list.length)return
      setSpheres(list)
      try{localStorage.setItem(SPHERES_CACHE_KEY,JSON.stringify(list))}catch{}
    }).catch(()=>{})
    return()=>{live=false}
  },[])
  return spheres
}

// Послание дня: одна карта в сутки на пользователя. Текст пишет бэкенд
// (POST /api/v1/daily/message) и хранит по одной записи на пользователя, поэтому
// повторное нажатие в тот же день возвращает то же послание без новой генерации.
// Само послание показывается НА карте: карта переворачивается, на рубашке — текст.
function Home({go,chooseTopic}){
 const spheres=useTarotSpheres()
 const [ctaPulse,setCtaPulse]=useState(false)
 const [daily,setDaily]=useState(null)
 const [flipped,setFlipped]=useState(false)
 const [loading,setLoading]=useState(false)
 const [error,setError]=useState('')
 const [now,setNow]=useState(()=>Date.now())
 // Сегодняшнее послание подтягиваем при входе: если оно уже открывалось, карта
 // переворачивается мгновенно, без запроса к нейросети.
 useEffect(()=>{
   let live=true
   getDailyMessage().then(data=>{if(live&&data?.is_today)setDaily(data)}).catch(()=>{})
   return()=>{live=false}
 },[])
 const reveal=async()=>{
   if(loading)return
   setError('')
   if(daily){setFlipped(true);return}
   setLoading(true)
   try{
     const data=await drawDailyMessage()
     setDaily(data)
     setFlipped(true)
   }catch(e){setError(e?.message||'Не удалось получить послание. Попробуй ещё раз.')}
   finally{setLoading(false)}
 }
 useEffect(()=>{
   if(!ctaPulse)return
   const timer=setTimeout(()=>{
     setCtaPulse(false)
     reveal()
   },330)
   return()=>clearTimeout(timer)
 },[ctaPulse])
 // Обратный отсчёт до следующего послания тикает раз в минуту и только пока
 // карта открыта — на закрытой карте подписи всё равно не видно.
 useEffect(()=>{
   if(!flipped||!daily?.is_today)return
   setNow(Date.now())
   const timer=setInterval(()=>setNow(Date.now()),60000)
   return()=>clearInterval(timer)
 },[flipped,daily?.is_today])
 const nextIn=daily?.is_today&&daily.next_available_at?untilLabel(daily.next_available_at,now):''
 return <section className="screen home-screen">
   <section className="hero">
    <a className="hero-social hero-social--telegram" href="https://t.me/ezoterikonlain" target="_blank" rel="noreferrer" aria-label="Telegram"/>
    <a className="hero-social hero-social--instagram" href="https://www.instagram.com/zakxarovavika" target="_blank" rel="noreferrer" aria-label="Instagram"/>
    <div className={`hero-card ${flipped?'is-flipped':''}`}>
     <button className="hero-card-face hero-card-front" onClick={reveal} disabled={loading||flipped} aria-label={daily?'Открыть послание дня':'Вытянуть карту послания дня'}/>
     <button className="hero-card-face hero-card-back" onClick={()=>setFlipped(false)} disabled={!flipped} aria-label="Закрыть послание дня">
      <span className="hero-card-kicker">Послание дня</span>
      <span className="hero-card-text">{daily?.message}</span>
      {daily?.card&&<small className="hero-card-name">{daily.card}</small>}
      {nextIn&&<small className="hero-card-next">Следующее послание {nextIn}</small>}
     </button>
     {loading&&<span className="hero-card-loading"><span className="button-spinner"/>вытягиваем карту…</span>}
    </div>
    <button className={`hero-hotspot ${ctaPulse?'is-animating':''}`} onClick={()=>setCtaPulse(true)} disabled={loading} aria-label="Узнать послание"/>
   </section>
   {error&&<p className="api-error hero-error" role="alert">{error}</p>}
   <Glass className="home-panel">
    <h2>Таро-расклады</h2><p className="subtitle">Что тебя беспокоит?</p>
    <div className="category-grid">
      {spheres.filter(s=>s!==PSY_SPHERE).slice(0,3).map(s=><button key={s} className="press" onClick={()=>chooseTopic(s)}><b>{s}</b><span>{SPHERE_SUBTITLES[s]||'Персональные расклады карт'}</span></button>)}
      <button className="press" onClick={()=>chooseTopic(PSY_SPHERE)}><b>Я не знаю, что<br/>со мной</b><span>Помоги понять,<br/>что меня беспокоит</span></button>
    </div>
   </Glass>
   <Glass className="astro-panel"><h2>Астро-разборы</h2><p className="subtitle">Что тебя беспокоит?</p><Pill onClick={()=>go(screens.ASTRO)}>Я хочу разобрать формулу души</Pill></Glass>
   <Glass className="chat-panel"><h2>Clavis</h2><p>Задай любой вопрос картам без расклада или продолжи диалог.</p><Pill onClick={()=>startCardsChat().then(()=>go(screens.CHAT)).catch(()=>go(screens.CHAT))}>Спросить карты</Pill></Glass>
   <Glass className="chat-panel"><h2>Сонник</h2><p>Опиши сон и обсуди его смысл в чате Clavis.</p><Pill onClick={()=>go(screens.DREAM)}>Разобрать сон</Pill></Glass>
   <Glass className="chat-panel"><h2>Тарифы</h2><p>Подписка на расклады, пакеты астро-разборов и вопросы к ИИ.</p><Pill onClick={()=>go(screens.TARIFFS)}>Смотреть тарифы</Pill></Glass>
   <Pill className="all-topics" onClick={()=>go(screens.CATEGORIES)}>Все темы и направления</Pill>
 </section>
}

// Внизу списка — фиксированные пункты (астрология и бот), а сферы над ними
// приходят из каталога: новые сферы появятся здесь сами. «Психология»
// показывается под пользовательским названием «Я не знаю, что со мной».
const EXTRA_TOPICS=[['Хочу узнать про себя','Астрология и формула души','astro'],['Не нашла свой вопрос','Спроси таро-бота','bot'],['Сонник','Разобрать сон в чате Clavis','dream']]
function Categories({go,chooseTopic}){
  const spheres=useTarotSpheres()
  const rows=[...spheres.map(s=>[displaySphere(s),s===PSY_SPHERE?'Психологические расклады — помоги понять, что меня беспокоит':(SPHERE_SUBTITLES[s]||'Персональные расклады карт'),'sphere',s]),...EXTRA_TOPICS]
  return <section className="screen categories-screen"><h2 className="page-title">Что тебя беспокоит?</h2><p className="subtitle">Выбери сферу, и я сделаю расклад</p><SparkLine/><div className="topic-list">{rows.map(([title,copy,kind,sphere])=><button className="press" key={title} onClick={()=>kind==='sphere'?chooseTopic(sphere):kind==='astro'?go(screens.ASTRO):kind==='dream'?go(screens.DREAM):go(screens.BOT)}><Orb/><span><b>{title}</b><small>{copy}</small></span><ChevronRight/></button>)}</div><Glass className="question-card"><div><b>Не нашли свой вопрос?</b><span>Спроси меня в чате — я помогу</span></div><Pill onClick={()=>go(screens.CHAT)}>Написать</Pill></Glass></section>
}

// Каталог раскладов — ТОЛЬКО с бэка (его правит заказчица в админке).
// Захардкоженных названий больше нет: из-за них заголовок и вопросы могли быть
// от разных раскладов — локальный заголовок не находился в каталоге, и открывался
// первый попавшийся расклад сферы. Кэш на сферу — чтобы список открывался
// мгновенно, как астро-каталог; App префетчит его при старте.
const TAROT_CACHE_PREFIX='tarotSpreads:'
const LIST_BASE_COUNT=6
function readTarotCache(sphere){
  try{const c=JSON.parse(localStorage.getItem(TAROT_CACHE_PREFIX+sphere));return Array.isArray(c)&&c.length?c:null}catch{return null}
}
// Сферы берём с бэка и для каждой греем кэш раскладов — новые сферы каталога
// префетчатся сами, без правок фронта.
export function prefetchTarotCatalogue(){
  return fetchTarotSpheres().then(spheres=>{
    if(!Array.isArray(spheres)||!spheres.length)return
    try{localStorage.setItem(SPHERES_CACHE_KEY,JSON.stringify(spheres))}catch{}
    return Promise.all(spheres.map(sphere=>fetchTarotSpreads(sphere)
      .then(list=>{if(Array.isArray(list)&&list.length)try{localStorage.setItem(TAROT_CACHE_PREFIX+sphere,JSON.stringify(list))}catch{}})
      .catch(()=>{})))
  }).catch(()=>{})
}
// «Записаться на консультацию» открывает личную переписку с заказчицей
// в Telegram (SDK openTelegramLink), а не внутренний чат.
const CONSULT_TG_LINK='https://t.me/viki8i8'
function Consult(){return <Glass className="consult"><Orb/><div><b>Можем пообщаться лично!</b><span>Если ты хочешь обсудить ситуацию, я всегда рада тебя выслушать</span></div><Pill onClick={()=>openTelegramLink(CONSULT_TG_LINK)}>Записаться на консультацию</Pill></Glass>}
function List({go,topic,openReading,access}){
  const [more,setMore]=useState(false)
  const [moreAnimating,setMoreAnimating]=useState(false)
  // null — идёт первая загрузка (кэша нет); [] — каталог пуст или ошибка сети
  const [spreads,setSpreads]=useState(()=>readTarotCache(topic))
  const [error,setError]=useState('')
  const [purchase,setPurchase]=useState(null)
  useEffect(()=>{
    let live=true
    const cached=readTarotCache(topic)
    setSpreads(cached);setError('');setMore(false)
    fetchTarotSpreads(topic).then(rows=>{
      if(!live)return
      const list=Array.isArray(rows)?rows:[]
      setSpreads(list)
      try{localStorage.setItem(TAROT_CACHE_PREFIX+topic,JSON.stringify(list))}catch{}
    }).catch(()=>{
      if(live&&!cached){setSpreads([]);setError('Не удалось загрузить расклады. Проверь соединение и попробуй ещё раз.')}
    })
    return()=>{live=false}
  },[topic])
  // Замки — из GET /users/me/access: spread_ids перечисляет расклады, открытые
  // живой подпиской. Пока доступ не загрузился (access === null), замков не рисуем:
  // мигание «всё закрыто → всё открыто» на старте хуже, чем 402 при попытке войти,
  // который всё равно обработан.
  const open=access?new Set(access.spread_ids):null
  const rows=(spreads||[]).map(sp=>({spread:sp,locked:open?!open.has(sp.id):false}))
  const visible=more?rows:rows.slice(0,LIST_BASE_COUNT)
  const openItem=item=>item.locked
    ?setPurchase(new ApiError(402,'',ACCESS_CODES.TAROT))
    :openReading(item.spread.title,undefined,item.spread)
  const toggleMore=()=>{setMore(v=>!v);setMoreAnimating(true)}
  return <section className="screen list-screen">
    <Topbar go={go} back={screens.CATEGORIES} label={topic}/>
    <h2 className="page-title">{topic===PSY_SPHERE?'Психологические расклады':`Расклады: ${topic.toLowerCase()}`}</h2>
    <p className="subtitle">Выбери вопрос, и я сделаю расклад</p>
    <SparkLine/>
    {error&&<p className="api-error" role="alert">{error}</p>}
    {spreads===null&&!error&&<p className="result-placeholder">✨ Загружаем расклады…</p>}
    {Array.isArray(spreads)&&!rows.length&&!error&&<p className="result-placeholder">В этой сфере пока нет раскладов.</p>}
    <div className="reading-list">{visible.map((item,index)=><button className={`reading-row press ${index>=LIST_BASE_COUNT?'extra-reading':''}`} key={item.spread.id} onClick={()=>openItem(item)}><Orb/><span><b>{item.spread.title}{item.locked&&<LockKeyhole size={14}/>}</b><small>{item.spread.description||'Персональный расклад карт'}</small></span>{item.locked?<em>по подписке</em>:<ChevronRight/>}</button>)}</div>
    {rows.length>LIST_BASE_COUNT&&<Pill className={`show-more ${more?'is-expanded':''} ${moreAnimating?'is-toggling':''}`} onClick={toggleMore} onAnimationEnd={()=>setMoreAnimating(false)} aria-expanded={more}>{more?'Скрыть дополнительные':'Показать еще расклады'} <ChevronDown size={17}/></Pill>}
    <Glass className="question-card"><div><b>Не нашли свой вопрос?</b><span>Спроси меня в чате — я помогу</span></div><Pill onClick={()=>go(screens.BOT)}>Написать</Pill></Glass>
    {purchase&&<Paywall error={purchase} onClose={()=>setPurchase(null)} onBuy={()=>{setPurchase(null);go(screens.TARIFFS)}}/>}
  </section>
}

const SITUATION_MAX=3000
// Ручной ввод даты: ДД.ММ.ГГГГ ⇄ ISO YYYY-MM-DD, с проверкой реальной даты.
const isoToRuDate=iso=>{const m=/^(\d{4})-(\d{2})-(\d{2})$/.exec(iso||'');return m?`${m[3]}.${m[2]}.${m[1]}`:''}
const maskRuDate=raw=>{const d=String(raw).replace(/\D/g,'').slice(0,8);return [d.slice(0,2),d.slice(2,4),d.slice(4,8)].filter(Boolean).join('.')}
const ruDateToIso=masked=>{
  const m=/^(\d{2})\.(\d{2})\.(\d{4})$/.exec(masked||'')
  if(!m)return ''
  const day=+m[1],mon=+m[2],year=+m[3]
  if(mon<1||mon>12||day<1||day>31||year<1900||year>new Date().getFullYear())return ''
  const iso=`${m[3]}-${m[2]}-${m[1]}`
  const dt=new Date(iso)
  return dt.getUTCFullYear()===year&&dt.getUTCMonth()+1===mon&&dt.getUTCDate()===day?iso:''
}
// Форма-шаг перед картами: имя, дата рождения и ситуация — ровно то, что
// требует POST /api/v1/tarot/readings. Для раздела «Отношения» дополнительно
// собираем данные партнёра и подмешиваем их в situation (в API Таро отдельных
// полей партнёра нет — они есть только у астро).
// Прогрев картинок перед показом результата: страница расклада появляется,
// когда лица карт уже в кэше. Таймаут — чтобы битая/медленная картинка
// не держала пользователя на лоадере вечно.
const preloadImages=(urls,timeoutMs=8000)=>Promise.race([
  Promise.all(urls.map(src=>new Promise(resolve=>{const img=new Image();img.onload=img.onerror=()=>resolve();img.src=src}))),
  new Promise(resolve=>setTimeout(resolve,timeoutMs)),
])

// Поля партнёра показываем только там, где расклад про конкретного мужчину
// (он/бывший/муж/мы…). Для раскладов «про одинокую» («Когда я встречу свою
// любовь?», «Почему я до сих пор одна?») имя партнёра спрашивать не у кого —
// прямое замечание заказчицы. \b с кириллицей в JS не работает, поэтому
// границы слов размечены вручную через [^а-яё].
const PARTNER_RE=/(^|[^а-яё])(он|его|ему|нему|ним|нём|нем|мы|нас|нам)(?![а-яё])|(^|[^а-яё])муж(а|у|ем|е)?(?![а-яё])|(^|[^а-яё])наш|эти отношения|бывш|женат|партн|расста|расстояни|измен|ревну|брос|двумя|вместе/
const spreadAboutPartner=spread=>PARTNER_RE.test(String(spread?.title||'').toLowerCase())

function TarotIntake({onSubmit,showPartner=false,spread,onSelectTopic,selecting=false}){
  const tgName=window.Telegram?.WebApp?.initDataUnsafe?.user?.first_name||''
  const [name,setName]=useState(()=>tgName||localStorage.getItem('tarotName')||'')
  const [dateText,setDateText]=useState(()=>{try{return isoToRuDate(normalizeBirthDate(JSON.parse(localStorage.getItem('birthData'))?.date))}catch{return''}})
  const [partnerName,setPartnerName]=useState('')
  const [partnerDateText,setPartnerDateText]=useState('')
  const [situation,setSituation]=useState('')
  const [error,setError]=useState('')
  const situationTrimmed=situation.trim()
  const birthDate=ruDateToIso(dateText)
  const partnerBirthDate=ruDateToIso(partnerDateText)
  const partnerDateOk=!partnerDateText||Boolean(partnerBirthDate)
  const valid=name.trim().length>0&&name.trim().length<=100&&Boolean(birthDate)&&situationTrimmed.length>0&&partnerDateOk
  const submit=e=>{
    e.preventDefault()
    if(!name.trim())return setError('Как тебя зовут?')
    if(!birthDate)return setError('Введи дату рождения в формате ДД.ММ.ГГГГ')
    if(showPartner&&partnerDateText&&!partnerBirthDate)return setError('Проверь дату рождения партнёра (ДД.ММ.ГГГГ)')
    if(!situationTrimmed)return setError('Опиши, что случилось — так карты ответят точнее')
    let finalSituation=situationTrimmed
    if(showPartner){
      const bits=[]
      if(partnerName.trim())bits.push(`имя ${partnerName.trim()}`)
      if(partnerBirthDate)bits.push(`дата рождения ${isoToRuDate(partnerBirthDate)}`)
      if(bits.length)finalSituation=`${situationTrimmed}\n\nПартнёр: ${bits.join(', ')}.`.slice(0,SITUATION_MAX)
    }
    onSubmit({name:name.trim(),birthDate,situation:finalSituation})
  }
  return <form className="tarot-intake" onSubmit={submit}>
    <Glass className="form-card">
      {spread?.questions?.length>0&&<p className="birth-points"><b>Карты ответят на ({spread.questions.length}):</b>{spread.questions.map((q,i)=><span key={`${q}-${i}`}>{i+1}. {q}</span>)}</p>}
      <label>Как тебя зовут?<div><CircleUserRound/><input value={name} onChange={e=>setName(e.target.value.slice(0,100))} placeholder="Имя" aria-label="Имя"/></div></label>
      <label>Дата рождения<div><CalendarDays/><input inputMode="numeric" value={dateText} onChange={e=>setDateText(maskRuDate(e.target.value))} placeholder="ДД.ММ.ГГГГ" aria-label="Дата рождения"/></div></label>
      {showPartner&&<>
        <label>Имя партнёра <small>если есть</small><div><CircleUserRound/><input value={partnerName} onChange={e=>setPartnerName(e.target.value.slice(0,100))} placeholder="Имя партнёра" aria-label="Имя партнёра"/></div></label>
        <label>Дата рождения партнёра <small>если знаешь</small><div><CalendarDays/><input inputMode="numeric" value={partnerDateText} onChange={e=>setPartnerDateText(maskRuDate(e.target.value))} placeholder="ДД.ММ.ГГГГ" aria-label="Дата рождения партнёра"/></div></label>
      </>}
      <label>Что случилось?<div className="intake-situation"><textarea value={situation} onChange={e=>setSituation(e.target.value.slice(0,SITUATION_MAX))} placeholder="Опиши свою ситуацию и что хочешь понять с помощью карт" rows={4} aria-label="Опиши ситуацию"/></div><small className="intake-counter">{situation.length} / {SITUATION_MAX}</small></label>
      {error&&<p className="form-error" role="alert">{error}</p>}
    </Glass>
    <Pill className="primary" type="submit" disabled={!valid}><Sparkles size={18}/> Открыть карты <Sparkles size={18}/></Pill>
    {onSelectTopic&&<Pill className="chat-topic" type="button" disabled={selecting} onClick={onSelectTopic}>{selecting?'Выбираем…':'Выбрать темой чата без расклада'}</Pill>}
  </form>
}

function Reading({go,topic,title,spread,onAccessChanged}){
  // Разбор нейросеть делает СРАЗУ после отправки формы: на экран карт
  // пользователь попадает уже с готовым текстом в «Разборе расклада».
  // Карты каждого вопроса показываются сразу открытыми (картинки карт).
  const [folded,setFolded]=useState({})
  const [activeSpread,setActiveSpread]=useState(spread||null)
  const [result,setResult]=useState(null)
  const [loading,setLoading]=useState(false)
  const [error,setError]=useState('')
  const [selecting,setSelecting]=useState(false)
  const [paywall,setPaywall]=useState(null)
  // Введённые пользователем данные (имя/дата/ситуация). Пока их нет —
  // всегда показываем форму, независимо от прошлых раскладов.
  const [intake,setIntake]=useState(null)

  useEffect(()=>{
    let live=true
    if(spread?.id){setActiveSpread(spread);selectTarotSpread(spread.id).catch(()=>{});return()=>{live=false}}
    // Вход без объекта расклада (перезагрузка на #reading): ищем его в каталоге
    // ТОЛЬКО по названию. Фолбэка на первый расклад сферы больше нет — из-за
    // него заголовок и вопросы могли оказаться от разных раскладов.
    fetchTarotSpreads(topic==='Общий расклад'?null:topic).then(rows=>{
      if(!live)return
      const wanted=String(title||'').trim().toLowerCase()
      const found=wanted?rows?.find(item=>item.title.toLowerCase()===wanted)||rows?.find(item=>item.title.toLowerCase().includes(wanted)):null
      if(found)setActiveSpread(found)
      else setError('Не нашли этот расклад в каталоге. Вернись назад и выбери расклад из списка.')
    }).catch(()=>{})
    return()=>{live=false}
  },[spread?.id,topic,title])

  const generate=async values=>{
    if(!values||loading)return
    setLoading(true);setError('')
    try{
      // activeSpread мог не успеть догрузиться к отправке формы — добираем
      // каталог прямо здесь, чтобы генерация не зависела от этой гонки.
      let sp=activeSpread
      if(!sp?.id){
        const rows=await fetchTarotSpreads(topic==='Общий расклад'?null:topic).catch(()=>null)
        const wanted=String(title||'').trim().toLowerCase()
        const found=wanted?rows?.find(item=>item.title.toLowerCase()===wanted)||rows?.find(item=>item.title.toLowerCase().includes(wanted)):null
        if(found){sp=found;setActiveSpread(found)}
      }
      if(!sp?.id)throw new Error('Не нашли этот расклад в каталоге. Вернись назад и выбери его из списка.')
      const data=await generateTarotReading(sp.id,values.name,values.birthDate,values.situation)
      // Страницу показываем только с прогретыми картинками выпавших карт
      const faces=[...new Set((data?.reading?.answers||[]).flatMap(a=>a.cards||[]).map(cardFace))]
      await preloadImages(faces)
      setResult(data)
      setFolded({})
    }catch(e){
      // 402 — расклад не входит в подписку (или её нет). Это не ошибка сети:
      // показываем предложение купить, а форму оставляем заполненной, чтобы после
      // покупки не вводить всё заново.
      if(isAccessError(e)){setPaywall(e);setIntake(null)}
      else setError(e?.message||'Не удалось получить разбор. Попробуй ещё раз.')
    }finally{setLoading(false)}
  }

  // Выбрать расклад темой AI-чата без генерации (POST /select) — как в шаблоне
  const selectAsTopic=async()=>{
    if(selecting)return
    setSelecting(true);setError('')
    try{
      if(!activeSpread?.id)throw new Error('Расклад ещё загружается — попробуй через пару секунд.')
      await selectTarotSpread(activeSpread.id)
      go(screens.CHAT)
    }catch(e){
      if(isAccessError(e))setPaywall(e)
      else setError(e?.message||'Не удалось выбрать тему чата')
    }
    finally{setSelecting(false)}
  }

  const answers=result?.reading?.answers||[]
  const stepTitles=activeSpread?.questions?.slice(0,3)||['Что происходит сейчас','Причина ситуации','Что будет дальше']
  // Модуль на каждый вопрос: ряд из 3 карт этого вопроса → его трактовка.
  // Пять вопросов — пять таких модулей подряд, дизайн секции одинаковый.
  const answerBlocks=answers.map((a,i)=>({key:String(i),title:a.question||activeSpread?.questions?.[i]||`Карта ${i+1}`,cards:a.cards||[],text:a.interpretation}))
  const extraBlocks=result?[
    ...(result.reading?.summary?[{key:'summary',title:'Итог расклада',cards:[],text:result.reading.summary}]:[]),
    ...(result.reading?.advice?[{key:'advice',title:'Совет карт',cards:[],text:result.reading.advice}]:[]),
  ]:[]
  const renderBlock=b=><article key={b.key} className={`result-block ${folded[b.key]?'':'open'}`}>
    <button type="button" className="press" onClick={()=>setFolded(f=>({...f,[b.key]:!f[b.key]}))} aria-expanded={!folded[b.key]}>
      <b>{b.title}</b>
      {folded[b.key]&&<p>Нажми, чтобы открыть толкование</p>}
      <ChevronDown size={18}/>
    </button>
    {!folded[b.key]&&<div className="result-block-body">{b.cards.length>0&&<p className="result-cards">{b.cards.join(' · ')}</p>}<Markdown text={b.text}/></div>}
  </article>
  // Форму показываем, пока пользователь не ввёл свои данные для этого расклада.
  const needIntake=!intake
  const submitIntake=values=>{
    try{const prev=JSON.parse(localStorage.getItem('birthData'))||{};localStorage.setItem('birthData',JSON.stringify({...prev,date:values.birthDate}))}
    catch{localStorage.setItem('birthData',JSON.stringify({date:values.birthDate}))}
    localStorage.setItem('tarotName',values.name)
    setIntake(values)
    generate(values)
  }
  return <section className="screen reading-screen">
    <Topbar go={go} back={screens.LIST} label={topic}/>
    <h2 className="page-title">{title||activeSpread?.title||'Расклад'}</h2><SparkLine/>
    {needIntake?<>
      {error&&<p className="api-error" role="alert">{error}</p>}
      <TarotIntake onSubmit={submitIntake} showPartner={topic==='Отношения'&&spreadAboutPartner(activeSpread)} spread={activeSpread} onSelectTopic={selectAsTopic} selecting={selecting}/>
    </>:<>
      {/* Карты переворачиваются тем же 3D-флипом, что и карта послания дня на главной:
          рубашка и лицо — две грани одной карты, а не подмена картинки. */}
      {/* Без .press: его filter/scale на кнопке расплющивают 3D внутри и дёргают
          поворот — отклик на тап даёт сам переворот карты. */}
      {error&&<p className="api-error" role="alert">{error}</p>}
      {error&&!loading&&!result&&<Pill className="retry-reading" onClick={()=>generate(intake)}>Попробовать ещё раз</Pill>}
      {!result&&<>
        <div className="cards-pick">{[0,1,2].map(i=><button key={i} disabled><span className="card-aura"/><span className="card-flip"><img className="card-face card-back" src={`${A}tarot-card.webp`} alt={`Карта ${i+1}`}/></span></button>)}</div>
        {loading&&<p className="hint">раскладываем карты…</p>}
        <div className="steps">{stepTitles.map((t,i)=><span key={`${t}-${i}`}><b>{i+1}</b>{t}</span>)}</div>
        <Glass className="result"><h3>Разбор расклада</h3>
          <p className="result-placeholder">{loading?'Раскладываем карты и готовим персональный разбор…':'Здесь появится твой персональный разбор.'}</p>
        </Glass>
      </>}
      {result&&answerBlocks.map((b,qi)=><React.Fragment key={b.key}>
        {/* Карты сразу открыты (просьба заказчицы): лицо — картинка карты из
            админки (frontend/public/cards), без переворотов. */}
        <div className={`cards-pick${b.cards.length&&b.cards.length<3?' is-short':''}`}>{b.cards.map((name,ci)=>{const reversed=/\(перев/i.test(name);return <figure key={ci} className={`card-open${reversed?' is-reversed':''}`}><img src={cardFace(name)} alt={name}/></figure>})}</div>
        <div className="steps is-short"><span className="active"><b>{qi+1}</b>{b.title}</span></div>
        <Glass className="result">
          {qi===0&&<h3>Разбор расклада</h3>}
          {qi===0&&result?.reading?.greeting&&<div className="result-greeting"><Markdown text={result.reading.greeting}/></div>}
          {renderBlock(b)}
          {qi===answerBlocks.length-1&&extraBlocks.map(renderBlock)}
        </Glass>
      </React.Fragment>)}
    </>}
    <Consult/>
    {loading&&<AiModal loading title="Читаем карты" subtitle="Это может занять 15–30 секунд"/>}
    {paywall&&<Paywall error={paywall} onClose={()=>{setPaywall(null);onAccessChanged?.()}} onBuy={()=>{setPaywall(null);go(screens.TARIFFS)}}/>}
  </section>
}

function Chat({go,access,onAccessChanged}){
  const [text,setText]=useState('')
  const spheres=useTarotSpheres()
  const [contextCollapsed,setContextCollapsed]=useState(true)
  const [topicPickerOpen,setTopicPickerOpen]=useState(false)
  const [messages,setMessages]=useState([])
  const [loading,setLoading]=useState(true)
  const [clearing,setClearing]=useState(false)
  const [confirmClear,setConfirmClear]=useState(false)
  const messagesRef=useRef(null)
  const [session,setSession]=useState(null)
  const [paywall,setPaywall]=useState(null)
  // Остаток вопросов. Стартует из общего доступа (App), дальше обновляется полем
  // `balance` каждого ответа — так счётчик под полем ввода не отстаёт на сообщение
  // и не требует отдельного запроса после каждой отправки.
  const [balance,setBalance]=useState(access?.balance||null)
  useEffect(()=>{if(access?.balance)setBalance(b=>b||access.balance)},[access])
  useEffect(()=>{
    let live=true
    const dreamBootstrap=claimDreamChat()
    const bootstrap=dreamBootstrap||claimGuidedChat()
    if(bootstrap){
      const {draft,promise}=bootstrap
      setSession(dreamBootstrap?{module:'dream',title:'Сонник',subtitle:'Разбор сновидений'}:{module:'cards',title:'Вопрос картам',subtitle:`Тема: ${draft.topic}`,topic:draft.topic})
      setMessages([{mine:true,text:draft.question,time:'сейчас'}])
      promise.then(({selection,response})=>{
        if(!live)return
        setSession(selection)
        setMessages([
          {mine:true,text:draft.question,time:'сейчас'},
          {mine:false,text:response?.reply||'Я получила твой вопрос. Давай разберём его глубже.',time:'сейчас'},
        ])
        if(response?.balance){setBalance(response.balance);onAccessChanged?.()}
      }).catch(e=>{
        if(!live)return
        // Вопрос из мастера уходит сразу при входе в чат — отказ по оплате тоже
        // ловим здесь, иначе он выглядел бы обрывом связи.
        if(isAccessError(e)){setPaywall(e);if(e.body?.balance)setBalance(e.body.balance)}
        else setMessages(m=>[...m,{mine:false,error:true,text:'Не удалось начать диалог. Проверь соединение и попробуй ещё раз.',time:'сейчас'}])
      }).finally(()=>{if(live)setLoading(false)})
    }else{
      Promise.all([getAiHistory(),getAiSession()]).then(async ([history,current])=>{
        if(!live)return
        if(!current)current=await startCardsChat()
        setSession(current)
        setMessages((history?.messages||[]).map(m=>({mine:m.role==='user',text:m.content,time:''})))
      }).catch(()=>{}).finally(()=>{if(live)setLoading(false)})
    }
    return()=>{live=false}
  },[])
  useEffect(()=>{messagesRef.current?.scrollTo({top:messagesRef.current.scrollHeight,behavior:messages.length?'smooth':'auto'})},[messages,loading])
  const send=async()=>{
    const prompt=text.trim()
    if(!prompt||loading)return
    setMessages(m=>[...m,{mine:true,text:prompt,time:'сейчас'}]);setText('');setLoading(true)
    try{
      let response
      try{response=await sendAiChat(prompt)}catch(error){if(error instanceof ApiError&&error.status===409){setSession(await startCardsChat());response=await sendAiChat(prompt)}else throw error}
      setMessages(m=>[...m,{mine:false,text:response?.reply||'Я получила твой вопрос. Давай разберём его глубже.',time:'сейчас'}])
      // Баланс в ответе — уже с учётом этого сообщения.
      if(response?.balance){setBalance(response.balance);onAccessChanged?.()}
    }catch(e){
      // 402 — либо тема закрыта (расклад/разбор не куплен), либо кончились вопросы.
      // Свой же вопрос из ленты убираем: он не отправлен и не списан, а висящее
      // без ответа сообщение выглядит как потерянное.
      if(isAccessError(e)){
        setMessages(m=>m.slice(0,-1))
        setText(prompt)
        setPaywall(e)
        if(e.body?.balance)setBalance(e.body.balance)
      }else setMessages(m=>[...m,{mine:false,error:true,text:'Не удалось получить ответ. Проверь соединение и попробуй ещё раз.',time:'сейчас'}])
    }
    finally{setLoading(false)}
  }
  const clear=async()=>{
    if(clearing||messages.length===0)return
    setClearing(true)
    try{await clearAiHistory();setMessages([]);setConfirmClear(false)}
    catch{setMessages(m=>[...m,{mine:false,error:true,text:'Не удалось очистить диалог. Попробуй ещё раз.',time:''}])}
    finally{setClearing(false)}
  }
  // Тема чата показывается панелью под шапкой: и для личного вопроса, и для
  // расклада/разбора, на который бэкенд заякорил сессию — иначе кажется, что «тема
  // не сохранилась», особенно когда в чат приходят из «Моих разборов».
  const contextTitle=!session?'':session.title==='Личный вопрос'
    ?(localStorage.getItem(GUIDED_NAME_KEY)||'Личный вопрос')
    :session.title
  // Завершить контекст расклада, разбора или сна и вернуться к свободным вопросам.
  const leaveTopic=async()=>{
    if(clearing)return
    setClearing(true)
    try{setSession(await startCardsChat());setMessages([])}
    catch{setMessages(m=>[...m,{mine:false,error:true,text:'Не удалось выйти из темы. Попробуй ещё раз.',time:''}])}
    finally{setClearing(false)}
  }
  const chooseChatTopic=async(topic)=>{
    if(clearing||loading)return
    if(topic===(session?.topic||null)){setTopicPickerOpen(false);return}
    setClearing(true)
    try{setSession(await startCardsChat(topic));setMessages([]);setTopicPickerOpen(false)}
    catch{setMessages(m=>[...m,{mine:false,error:true,text:'Не удалось выбрать тему. Попробуй ещё раз.',time:''}])}
    finally{setClearing(false)}
  }
  // Вопросы кончились — поле ввода не блокируем: пусть человек допишет мысль, а
  // отказ покажется по кнопке отправки. Блокировка ввода без объяснения читается
  // как поломка.
  const empty=balance!==null&&balance.total===0
  return <section className="screen chat-screen"><div className="chat-head"><Orb/><div className="chat-head-info"><h2>Clavis</h2>{!session&&<p>Задай любой вопрос картам</p>}</div><button className="clear-chat press" disabled={clearing||messages.length===0} onClick={()=>setConfirmClear(true)} aria-label="Очистить диалог"><Trash2/></button></div>{session?.module==='cards'
    ?<div className="chat-context is-collapsed"><div className="chat-context-head">
      <div className="chat-context-title"><span className="chat-context-kicker">{session.topic?'Выбранная тема':'Без темы'}</span><b>{session.topic?displaySphere(session.topic):'Вопрос картам'}</b></div>
      <button type="button" className="chat-context-toggle press" disabled={clearing} onClick={()=>setTopicPickerOpen(true)}>{session.topic?'Сменить тему':'Выбрать тему'}</button>
    </div></div>
    :session&&<div className={`chat-context${session.has_reading?' has-reading':''}${contextCollapsed?' is-collapsed':''}`}>
    <div className="chat-context-head">
      <div className="chat-context-title"><span className="chat-context-kicker">{CONTEXT_KIND[session.module]||'Тема'}</span><b>{contextTitle}</b></div>
      <button type="button" className="chat-context-toggle press" aria-expanded={!contextCollapsed} aria-controls="chat-context-details" onClick={()=>setContextCollapsed(value=>!value)}>{contextCollapsed?'Развернуть':'Свернуть'} <ChevronDown size={15}/></button>
    </div>
    <div id="chat-context-details" className="chat-context-details" hidden={contextCollapsed}>
    {session.subtitle&&<small>{session.subtitle}</small>}
    {/* Подставлен ли в тему готовый расклад/разбор — has_reading из GET /ai/session.
        Пользователю это не техническая деталь: от неё зависит, можно ли спрашивать
        «а что значит эта карта» или ИИ отвечает по теме вообще. */}
    <p>{session.module==='dream'?'Расскажи о сне и обсуди его образы.'
      :session.module==='general'?'Свободный разговор без расклада и разбора.'
      :session.has_reading?'Подставлен в чат целиком — спрашивай по нему что угодно.'
      :'Тема выбрана, расклада в ней ещё не было.'}</p>
    <div className="chat-context-actions">
      <button type="button" className="press chat-context-exit" disabled={clearing} onClick={leaveTopic}>Завершить тему</button>
    </div>
    </div>
  </div>}<div className="messages" ref={messagesRef}>{messages.length===0&&!loading&&<div className="chat-welcome">{session?.topic?`Задай вопрос по теме «${session.topic}». История сохранится здесь.`:session?.module==='dream'?'Расскажи о сне или задай уточняющий вопрос.':'Задай любой вопрос картам. История сохранится здесь.'}</div>}{messages.map((m,i)=><div key={i} className={`msg ${m.mine?'mine':''} ${m.error?'error':''}`}>{m.mine?m.text:<Markdown text={m.text}/>}<time>{m.time}</time></div>)}{loading&&<div className="typing">✦ ✦ ✦</div>}</div>{balance&&<div className={`chat-balance${empty?' is-empty':''}`}><span>{balanceLabel(balance)}</span>{empty&&<button className="press" onClick={()=>go?.(screens.TARIFFS)}>Докупить</button>}</div>}<form className="composer" onSubmit={e=>{e.preventDefault();send()}}><input value={text} onChange={e=>setText(e.target.value)} placeholder="Напиши свой вопрос..." aria-label="Напиши свой вопрос..."/><button className="press" disabled={loading||!text.trim()} aria-label="Отправить"><Send/></button></form>{confirmClear&&<AiModal kicker="История чата" title="Очистить диалог?" subtitle="Выбранный расклад и доступ останутся активными" onClose={()=>setConfirmClear(false)}><p>Все сообщения текущего диалога будут удалены без возможности восстановления.</p><div className="confirm-actions"><button className="press" onClick={()=>setConfirmClear(false)}>Отмена</button><button className="press danger" disabled={clearing} onClick={clear}>{clearing?'Очищаем…':'Очистить'}</button></div></AiModal>}{topicPickerOpen&&<CardsTopicPicker spheres={spheres} selected={session?.topic||null} saving={clearing} onPick={chooseChatTopic} onClose={()=>setTopicPickerOpen(false)}/>}{paywall&&<Paywall error={paywall} onClose={()=>setPaywall(null)} onBuy={()=>{setPaywall(null);go?.(screens.TARIFFS)}}/>}</section>
}

function Assistant(props){return <Chat {...props}/>}

function CardsTopicPicker({spheres,selected,saving,onPick,onClose}){
  return <AiModal kicker="Clavis" title="Выбери тему" subtitle="Можно говорить с картами по теме без расклада" onClose={onClose}>
    <p>При смене темы начнётся новый диалог. Вопросы без темы всегда доступны.</p>
    <div className="chat-topic-options">
      <button type="button" className="press" aria-pressed={!selected} disabled={saving} onClick={()=>onPick(null)}>Без темы</button>
      {spheres.map(sphere=><button key={sphere} type="button" className="press" aria-pressed={selected===sphere} disabled={saving} onClick={()=>onPick(sphere)}>{displaySphere(sphere)}</button>)}
    </div>
  </AiModal>
}

function DreamForm({go}){
  const [dream,setDream]=useState('')
  const submit=event=>{
    event.preventDefault()
    if(!dream.trim())return
    sessionStorage.setItem(DREAM_DRAFT_KEY,dream.trim())
    go(screens.CHAT)
  }
  return <section className="screen bot-screen dream-screen">
    <h2 className="page-title">Сонник</h2>
    <SparkLine/>
    <Glass className="chat-panel"><p>Опиши сон как помнишь: события, образы и чувства. После отправки откроется чат Clavis с разбором, и ты сможешь задать уточняющие вопросы.</p></Glass>
    <form className="wizard-actions" onSubmit={submit}>
      <label>Что тебе приснилось?<textarea value={dream} maxLength={2000} onChange={event=>setDream(event.target.value)} placeholder="Мне приснилось…" rows={7}/></label>
      <Pill type="submit" disabled={!dream.trim()}>Разобрать сон в чате Clavis</Pill>
    </form>
  </section>
}

function BotWizard({go}){const spheres=useTarotSpheres();const [step,setStep]=useState(0);const [topic,setTopic]=useState('Отношения');const [name,setName]=useState('');const [question,setQuestion]=useState('');const [typing,setTyping]=useState(false);const finish=e=>{e.preventDefault();if(!name.trim()||!question.trim())return;sessionStorage.setItem(GUIDED_DRAFT_KEY,JSON.stringify({name:name.trim(),topic,question:question.trim()}));go(screens.CHAT)};return <section className={`screen bot-screen${typing?' is-typing':''}`}><h2 className="page-title">Что тебя беспокоит?</h2>{!typing&&<div className="bot-dialog"><div className="bot-choice"><Orb/><span>{topic}</span></div>{step>=1&&<><b className="bot-label">Вика</b><div className="bot-bubble">Как я могу к тебе обращаться?</div></>}{step>=2&&<><b className="bot-label user">{name||'Ты'}</b><div className="bot-bubble user">Хочу задать личный вопрос</div><b className="bot-label">Вика</b><div className="bot-bubble">Напиши вопрос — я сразу передам имя, тему и запрос в чат Clavis.</div></>}</div>}{step===0&&<div className="wizard-actions"><p>Выбери тему вопроса</p><select value={topic} onChange={e=>setTopic(e.target.value)}>{spheres.map(s=><option key={s}>{displaySphere(s)}</option>)}</select><Pill onClick={()=>setStep(1)}>Продолжить</Pill></div>}{step===1&&<div className="wizard-actions"><label>Никнейм<input value={name} onChange={e=>setName(e.target.value)} onFocus={()=>setTyping(true)} onBlur={()=>setTyping(false)} placeholder="Как тебя зовут?"/></label><Pill disabled={!name.trim()} onClick={()=>setStep(2)}>Продолжить</Pill></div>}{step===2&&<form className="wizard-actions" onSubmit={finish}><label>Твой вопрос<textarea value={question} onChange={e=>setQuestion(e.target.value)} onFocus={()=>setTyping(true)} onBlur={()=>setTyping(false)} placeholder="Напиши свой вопрос..."/></label><Pill type="submit" disabled={!question.trim()}>Перейти в чат Clavis</Pill></form>}</section>}

// Каталог астро-разборов: все разборы с бэка, сгруппированные по разделам.
// До этого экрана не было — Birth сам молча выбирал первый подходящий разбор,
// поэтому пользователю был доступен ровно один из всего каталога.
// Каталог астро-разборов кэшируется, чтобы экран открывался сразу с готовым
// контентом, без «Загружаем…». App ещё и префетчит его при старте приложения.
const ASTRO_CACHE_KEY='astroCatalogue'
function groupAstroReadings(list){
  const grouped=new Map()
  for(const item of list||[]){
    if(!grouped.has(item.section))grouped.set(item.section,[])
    grouped.get(item.section).push(item)
  }
  return [...grouped].map(([name,items])=>({name,items}))
}
function readAstroCache(){
  try{const c=JSON.parse(localStorage.getItem(ASTRO_CACHE_KEY));return Array.isArray(c)&&c.length?c:null}catch{return null}
}
export function prefetchAstroCatalogue(){
  return fetchAstroReadings()
    .then(list=>{try{localStorage.setItem(ASTRO_CACHE_KEY,JSON.stringify(groupAstroReadings(list)))}catch{}})
    .catch(()=>{})
}
function AstroCatalogue({go,onPick,access}){
  const cached=readAstroCache()
  const [sections,setSections]=useState(cached||[])
  const [error,setError]=useState('')
  const [loading,setLoading]=useState(!cached)
  const [paywall,setPaywall]=useState(null)
  // Как и в списке раскладов: замки берём из reading_ids, до загрузки доступа
  // ничего не запираем.
  const open=access?new Set(access.reading_ids):null
  useEffect(()=>{
    let live=true
    fetchAstroReadings()
      .then(list=>{
        if(!live)return
        const grouped=groupAstroReadings(list)
        setSections(grouped)
        try{localStorage.setItem(ASTRO_CACHE_KEY,JSON.stringify(grouped))}catch{}
      })
      .catch(e=>{if(live&&!cached)setError(e?.message||'Не удалось загрузить разборы')})
      .finally(()=>{if(live)setLoading(false)})
    return()=>{live=false}
  },[])
  const total=sections.reduce((sum,section)=>sum+section.items.length,0)
  return <section className="screen list-screen">
    <Topbar go={go} back={screens.CATEGORIES} label="Астрология"/>
    <h2 className="page-title">Астро-разборы</h2>
    <p className="subtitle">{total?`${nReadings(total)} по Формуле души — выбери свой`:loading?'Загружаем каталог…':'Каталог разборов пуст'}</p>
    <SparkLine/>
    {/* Сделанные разборы никуда не деваются — вход в их историю прямо из каталога,
        чтобы за старым разбором не приходилось идти через профиль. */}
    <Pill className="chat-topic" onClick={()=>go(screens.ARCHIVE)}>Мои разборы</Pill>
    {error&&<p className="api-error" role="alert">{error}</p>}
    {sections.map(section=><div className="catalogue-group" key={section.name}>
      <p className="catalogue-head">{section.name} · {section.items.length}</p>
      <div className="reading-list">{section.items.map(item=>{
        const locked=open?!open.has(item.id):false
        return <button className="reading-row press" key={item.id}
          onClick={()=>locked?setPaywall(new ApiError(402,'',ACCESS_CODES.ASTRO)):onPick(item)}>
          <Orb/>
          <span><b>{item.title}{locked&&<LockKeyhole size={14}/>}</b><small>{item.description||nPoints(item.points?.length||0)}</small></span>
          {locked?<em>по пакету</em>:<ChevronRight/>}
        </button>})}
      </div>
    </div>)}
    <Consult/>
    {paywall&&<Paywall error={paywall} onClose={()=>setPaywall(null)} onBuy={()=>{setPaywall(null);go(screens.TARIFFS)}}/>}
  </section>
}

// Поле города с подсказками — как CityField в одобренном шаблонном фронте,
// но в стилях нового дизайна. Свободный ввод любого города допустим.
function CityInput({value,onChange,ariaLabel}){
  const [open,setOpen]=useState(false)
  const items=open?suggestCities(value||''):[]
  return <>
    <MapPin/>
    <input value={value} onChange={e=>{onChange(e.target.value);setOpen(true)}} onFocus={()=>setOpen(true)} onBlur={()=>setOpen(false)} placeholder="Москва" autoComplete="off" aria-label={ariaLabel}/>
    <button type="button" className="field-action press" onClick={()=>onChange('')} aria-label={`Очистить: ${ariaLabel}`}><X/></button>
    {items.length>0&&<div className="city-suggest" onMouseDown={e=>e.preventDefault()}>{items.map(c=><div key={c} className="press" onClick={()=>{onChange(c);setOpen(false)}}>📍 {c}</div>)}</div>}
  </>
}

// Форма «Данные рождения» — паритет с одобренным шаблонным фронтом: имя, дата
// РУЧНЫМ вводом ДД.ММ.ГГГГ (нативный пикер клиентка забраковала), время, город
// с подсказками; для «Совместимости» — опциональный второй человек (partner_*
// поля POST /astro/readings); плюс «Выбрать темой чата без разбора».
function Birth({go,user,reading:picked,onAccessChanged}){
  const tgName=window.Telegram?.WebApp?.initDataUnsafe?.user?.first_name||''
  const [form,setForm]=useState(()=>{const s=readStoredBirth();return{name:tgName||localStorage.getItem('tarotName')||'',dateText:isoToRuDate(s.date),time:s.time,place:s.place}})
  const [withPartner,setWithPartner]=useState(false)
  const [partner,setPartner]=useState({name:'',dateText:'',time:'',place:''})
  const [error,setError]=useState('')
  const [loading,setLoading]=useState(false)
  const [selecting,setSelecting]=useState(false)
  const [paywall,setPaywall]=useState(null)
  const up=(key,value)=>setForm(v=>({...v,[key]:value}))
  const pUp=(key,value)=>setPartner(v=>({...v,[key]:value}))
  const isCompat=picked?.section==='Совместимость'
  const birthDate=ruDateToIso(form.dateText)
  const partnerDate=ruDateToIso(partner.dateText)
  const partnerFilled=partner.name.trim().length>0&&Boolean(partnerDate)&&partner.time.trim().length>0&&partner.place.trim().length>0
  const canSubmit=form.name.trim().length>0&&Boolean(birthDate)&&form.time.trim().length>0&&form.place.trim().length>0&&(!isCompat||!withPartner||partnerFilled)
  const submit=async()=>{
    if(!canSubmit){
      setError(form.dateText&&!birthDate?'Проверь дату рождения — формат ДД.ММ.ГГГГ':'Заполни все поля, чтобы получить точный разбор')
      return
    }
    setLoading(true);setError('')
    localStorage.setItem('birthData',JSON.stringify({date:birthDate,time:form.time,place:form.place}))
    localStorage.setItem('tarotName',form.name.trim())
    try{
      // Разбор приходит с каталога. Фолбэк нужен для входа прямо по #birth
      // (перезагрузка страницы, правка даты из профиля) — выбора там ещё нет.
      let reading=picked
      if(!reading){
        const readings=await fetchAstroReadings()
        reading=readings.find(item=>item.section==='Предназначение')||readings[0]
      }
      if(!reading)throw new Error('На сервере пока нет шаблона Формулы души')
      const birthCity=form.place.trim().split(',')[0].trim()
      const partnerPayload=isCompat&&withPartner?{name:partner.name.trim(),birthDate:partnerDate,birthTime:partner.time,birthCity:partner.place.trim().split(',')[0].trim()}:undefined
      const result=await generateAstroReading(reading.id,form.name.trim(),birthDate,form.time,birthCity,partnerPayload)
      localStorage.setItem('astroAiResult',JSON.stringify({reading,result}))
      // Разбор списал штуку из квоты пакета — обновляем доступ, чтобы счётчик
      // «осталось разборов» в профиле и замки в каталоге не показывали старое.
      onAccessChanged?.()
      go(screens.FORMULA)
    }catch(e){
      // 402 — либо пакет не куплен, либо разборы в нём кончились. Второе особенно
      // важно не показывать «ошибкой сети»: пользователь заплатил, просто всё истратил.
      if(isAccessError(e))setPaywall(e)
      else setError(e?.message||'Не удалось получить разбор. Попробуй ещё раз.')
    }
    finally{setLoading(false)}
  }
  // Выбрать разбор темой AI-чата без генерации (POST /select) — как в шаблоне
  const selectAsTopic=async()=>{
    if(!picked?.id||selecting)return
    setSelecting(true);setError('')
    try{await selectAstroReading(picked.id);go(screens.CHAT)}
    catch(e){
      if(isAccessError(e))setPaywall(e)
      else setError(e?.message||'Не удалось выбрать тему чата')
    }
    finally{setSelecting(false)}
  }
  return <section className="screen">
    {picked&&<Topbar go={go} back={screens.ASTRO} label={picked.section}/>}
    <h2 className="page-title">Данные рождения</h2>
    <p className="subtitle">{picked?picked.title:'Введите данные своего рождения'}</p>
    <SparkLine/>
    <Glass className="form-card">
      {picked?.points?.length>0&&<p className="birth-points"><b>Что разбираем ({picked.points.length}):</b>{picked.points.map((point,i)=><span key={`${point}-${i}`}>{i+1}. {point}</span>)}</p>}
      <label>Как тебя зовут?<div><CircleUserRound/><input value={form.name} onChange={e=>up('name',e.target.value.slice(0,100))} placeholder="Имя" aria-label="Имя"/></div></label>
      <label>Дата рождения<div><CalendarDays/><input inputMode="numeric" value={form.dateText} onChange={e=>up('dateText',maskRuDate(e.target.value))} placeholder="ДД.ММ.ГГГГ" aria-label="Дата рождения"/></div></label>
      <label>Время рождения <small>по местному времени</small><div><Clock3/><input type="time" value={form.time} onChange={e=>up('time',e.target.value)} aria-label="Время рождения"/></div></label>
      <label>Место рождения<div><CityInput value={form.place} onChange={v=>up('place',v)} ariaLabel="Место рождения"/></div></label>
      {isCompat&&<div className="partner-box">
        <button type="button" className="partner-toggle press" onClick={()=>setWithPartner(v=>!v)} aria-pressed={withPartner}>
          <span className={`partner-check${withPartner?' on':''}`}>{withPartner?'✓':''}</span>
          <span><b>Добавить второго человека</b><small>Необязательно — для разбора совместимости по двум Формулам Души</small></span>
        </button>
        {withPartner&&<>
          <label>Имя второго человека<div><CircleUserRound/><input value={partner.name} onChange={e=>pUp('name',e.target.value.slice(0,100))} placeholder="Имя" aria-label="Имя второго человека"/></div></label>
          <label>Дата рождения партнёра<div><CalendarDays/><input inputMode="numeric" value={partner.dateText} onChange={e=>pUp('dateText',maskRuDate(e.target.value))} placeholder="ДД.ММ.ГГГГ" aria-label="Дата рождения партнёра"/></div></label>
          <label>Время рождения партнёра<div><Clock3/><input type="time" value={partner.time} onChange={e=>pUp('time',e.target.value)} aria-label="Время рождения партнёра"/></div></label>
          <label>Город рождения партнёра<div><CityInput value={partner.place} onChange={v=>pUp('place',v)} ariaLabel="Город рождения партнёра"/></div></label>
        </>}
      </div>}
      {error&&<p className="form-error" role="alert">{error}</p>}
    </Glass>
    <Pill className="primary" disabled={loading} onClick={submit}>{loading?<><span className="button-spinner"/> Строим формулу…</>:<><Sparkles size={18}/> Получить разбор <Sparkles size={18}/></>}</Pill>
    {picked&&<Pill className="chat-topic" disabled={selecting} onClick={selectAsTopic}>{selecting?'Выбираем…':'Выбрать темой чата без разбора'}</Pill>}
    <SparkLine/>
    {paywall&&<Paywall error={paywall} onClose={()=>setPaywall(null)} onBuy={()=>{setPaywall(null);go(screens.TARIFFS)}}/>}
  </section>
}
// Результат Формулы души — как в одобренном шаблонном фронтенде: вверху вопрос
// каждого пункта разбора, под ним сразу ответ нейросети. Без модалок.
function Formula({go}){
  const payload=useMemo(()=>{try{return JSON.parse(localStorage.getItem('astroAiResult'))}catch{return null}},[])
  const result=payload?.result
  const points=result?.reading?.points||[]
  if(!result)return <section className="screen empty-formula"><h2 className="page-title">Твоя формула души</h2><SparkLine/><Orb/><p>Здесь появится твоя персональная Формула души.</p><Pill onClick={()=>go(screens.BIRTH)}>Рассчитать формулу</Pill></section>
  return <section className="screen formula-screen">
    <h2 className="page-title">Твоя формула души</h2>
    <p className="subtitle">{payload?.reading?.title||'Астрологический разбор'}</p>
    <SparkLine/>
    <img className="formula-wheel" src={`${A}formula-wheel.webp`} alt="Формула души"/>
    <Glass className="formula-greeting"><Markdown text={result.reading.greeting}/></Glass>
    {result.chart?.length>0&&<div className="formula-tags">{result.chart.map((item,i)=><span key={`${item}-${i}`}>{item}</span>)}</div>}
    {result.partner_chart?.length>0&&<div className="formula-tags">{result.partner_chart.map((item,i)=><span key={`${item}-${i}`}>{item}</span>)}</div>}
    <div className="formula-points">{points.map((point,i)=><Glass className="formula-point" key={`${point.point}-${i}`}>
      <span className="formula-point-num">Пункт {i+1}</span>
      <b>{point.point}</b>
      <div className="formula-point-copy"><Markdown text={point.interpretation}/></div>
    </Glass>)}</div>
    {result.reading.summary&&<Glass className="formula-total"><h3>Итог Формулы души</h3><div className="formula-point-copy"><Markdown text={result.reading.summary}/></div></Glass>}
    {/* Бэк уже сохранил разбор и вернул его id в history_id — кнопка просит прислать
        PDF в личку, а сам разбор в любом случае лежит в «Моих разборах». */}
    <SendPdfButton entryId={result.history_id}/>
    <Pill className="new-reading" onClick={()=>go(screens.CHAT)}>Обсудить разбор в чате Clavis</Pill>
    <Pill className="new-reading" onClick={()=>go(screens.ARCHIVE)}>Мои разборы</Pill>
    <Pill className="new-reading" onClick={()=>go(screens.ASTRO)}>Сделать новый разбор</Pill>
    <Consult/>
  </section>
}
function Profile({go,user,access,isAdmin,onAccessChanged}){
  const [accessOpen,setAccessOpen]=useState(false)
  // Сколько разборов уже сделано — карточка «Мои разборы» ведёт в их историю.
  // Отдельного счётчика на бэке нет, но список короткий (это покупки, а не лог),
  // поэтому считаем по первой странице; «99+» на случай, если её не хватило.
  const [readings,setReadings]=useState(null)
  useEffect(()=>{let live=true;Promise.all([fetchAstroHistory(null,99),fetchSavedTarotReadings(99)]).then(([astro,tarot])=>{if(live)setReadings((astro?.length??0)+(tarot?.length??0))}).catch(()=>{});return()=>{live=false}},[])
  // Ссылка и счётчик приглашённых приходят с бэка (GET /users/me/referrals): считать
  // рефералов на клиенте нечем, а ссылку собирает бэк — он знает username бота.
  const [referral,setReferral]=useState(null)
  const birthDate=useMemo(()=>{try{const iso=normalizeBirthDate(JSON.parse(localStorage.getItem('birthData'))?.date);return iso?formatBirthDate(iso):''}catch{return''}},[])
  useEffect(()=>{let live=true;getReferrals().then(data=>{if(live)setReferral(data)}).catch(()=>{});return()=>{live=false}},[])
  // Доступ App грузит один раз при старте, а профиль — как раз то место, где на него
  // смотрят: перечитываем при каждом заходе, чтобы срок подписки и остаток вопросов
  // были на момент открытия экрана, а не на момент запуска мини-аппы.
  useEffect(()=>{onAccessChanged?.()},[])
  const link=referral?.link||''
  // Сама ссылка нигде не показывается: по «Пригласить» открываем штатный диалог
  // Telegram «Поделиться» (t.me/share/url через SDK), где юзер выбирает чат.
  // Вне TG остаётся системный share, а если нет и его — буфер обмена.
  const share=async()=>{
    const text='Попробуй Дневник таролога — персональные расклады и разборы'
    if(!link){await showAlert(referral?'Ссылка недоступна: сервер не смог определить имя бота.':'Готовим твою ссылку, попробуй через пару секунд.');return}
    if(webApp.platform&&webApp.platform!=='unknown'){
      openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`)
      return
    }
    try{
      if(navigator.share)await navigator.share({title:'Дневник таролога',text,url:link})
      else await navigator.clipboard.writeText(link)
    }catch(e){if(e?.name!=='AbortError')await showAlert('Не удалось поделиться ссылкой')}
  }
  // Карточки профиля показывают реальные покупки: вопросы к ИИ — сквозной баланс с
  // бэка (бесплатные и платные раздельно), «мои доступы» — живые подписки и пакеты
  // с названием тарифа. Рублёвого кошелька больше нет: его эндпоинтов на сервере не
  // существовало, а доступ описывается покупками.
  const balance=access?.balance
  const cards=[
    {
      title:'Вопросы к ИИ',
      value:String(balance?.total??0),
      hint:balance?`${balance.free} ${plural(balance.free,'бесплатный','бесплатных','бесплатных')} · ${balance.paid} ${plural(balance.paid,'платный','платных','платных')}`:'',
      action:'Докупить',
      onClick:()=>go(screens.TARIFFS),
    },
    {
      title:'Мои разборы',
      value:readings===null?'—':readings>=99?'99+':String(readings),
      hint:readings?'Перечитать расклады и разборы':'Расклады и разборы сохраняются здесь',
      action:'Посмотреть',
      onClick:()=>go(screens.ARCHIVE),
    },
    {title:'Рефералы',value:String(referral?.count??0),hint:'',action:'Пригласить',onClick:share},
    {
      title:'Мои доступы',
      value:String(access?.entitlements?.length||0),
      hint:accessLabel(access),
      action:'Посмотреть',
      onClick:()=>setAccessOpen(true),
    },
  ]
  const wallets=walletsLabel(balance)
  return <section className="screen profile-screen"><h2 className="page-title">Профиль</h2><SparkLine/><Pill className="admin-entry" onClick={()=>go(screens.TARIFFS)}><CreditCard size={16}/> Тарифы</Pill>{isAdmin&&<Pill className="admin-entry" onClick={()=>go(screens.ADMIN)}><LockKeyhole size={16}/> Админка</Pill>}<div className="profile-head"><Avatar name={user.name} photo={user.photo}/><div><h3>{user.name}</h3>{user.username&&<p>@{user.username}</p>}<button className="press" onClick={()=>go(screens.BIRTH)}><CalendarDays/>{birthDate||'Указать дату рождения'}<Pencil/></button></div></div>{balance&&<p className="profile-balance">{balanceLabel(balance)}{wallets&&<small>{wallets}</small>}</p>}<Glass className="profile-cards">{cards.map((card,i)=><article key={card.title}><h3>{card.title}</h3><strong>{card.value}</strong>{card.hint&&<small>{card.hint}</small>}<Pill onClick={card.onClick}>{card.action}</Pill>{i===0?<WalletCards className="decoration"/>:<Orb className="decoration"/>}</article>)}</Glass>{accessOpen&&<AiModal kicker="Покупки" title="Мои доступы" subtitle="Что открыто прямо сейчас" onClose={()=>setAccessOpen(false)}>{access?.entitlements?.length?<div className="access-list">{access.entitlements.map(item=><article key={item.id}><CheckCircle2/><div><b>{entitlementTitle(item)}</b>{item.tariff_subtitle&&<span>{item.tariff_subtitle}</span>}<span>{entitlementNote(item)}</span></div></article>)}</div>:<p>Пока ничего не куплено. Загляни в тарифы — там подписка на Таро, астро-пакеты и вопросы к ИИ.</p>}{balance&&<p className="access-total">{balanceLabel(balance)}{wallets&&<small>{wallets}</small>}</p>}</AiModal>}</section>
}

// Обёртка над экраном «Мои разборы». Сам экран ничего не знает про витрину тарифов,
// а возврат к теме упирается в 402, если подписка кончилась или пакет не куплен, —
// поэтому Paywall (он живёт здесь) подключается снаружи одним колбэком.
function History({go,onAccessChanged}){
  const [paywall,setPaywall]=useState(null)
  return <>
    <HistoryScreen go={go} back={screens.HOME} astroScreen={screens.ASTRO}
      tarotScreen={screens.CATEGORIES} chatScreen={screens.CHAT} onAccessError={setPaywall}/>
    {paywall&&<Paywall error={paywall} onClose={()=>{setPaywall(null);onAccessChanged?.()}}
      onBuy={()=>{setPaywall(null);go(screens.TARIFFS)}}/>}
  </>
}

// Заглушка на время загрузки чанка админки: стили самой панели приезжают вместе с ним.
function AdminBoot({text}){return <section className="screen"><p style={{padding:'28px 0',textAlign:'center',opacity:.75}}>{text}</p></section>}

// Экран забаненного пользователя. Показывается вместо всего приложения: бэк отвечает
// 403 с кодом user_blocked на любой запрос такого пользователя, так что работать всё
// равно нечему — вместо череды «не удалось загрузить» показываем одну внятную причину.
function Blocked(){
  return <main className="app-shell"><section className="screen blocked-screen">
    <Orb/>
    <h2 className="page-title">Доступ закрыт</h2>
    <SparkLine/>
    <Glass className="blocked-card">
      <LockKeyhole/>
      <p>Ваш аккаунт заблокирован администратором, поэтому расклады, разборы и чат недоступны.</p>
      <p className="blocked-hint">Если это ошибка — напишите в поддержку, блокировку снимут.</p>
    </Glass>
  </section></main>
}

// Экраны, на которые можно привести человека снаружи — параметром `?screen=` в адресе
// мини-аппа (кнопка «открыть приложение» под рассылкой, см.
// backend/services/broadcast.py). Список закрытый, и в нём только те экраны, которым
// не нужно состояние с предыдущего: `reading` без выбранного расклада и `birth` без
// выбранного разбора открылись бы пустыми, а `admin` — вообще не для всех.
const LINKABLE=[screens.HOME,screens.CATEGORIES,screens.ASTRO,screens.TARIFFS,screens.CHAT,screens.ARCHIVE,screens.FORMULA,screens.PROFILE]

function App(){
  // Маршрут живёт в якоре, но при запуске мини-аппа якорь занимает Telegram своим
  // `tgWebAppData=…` — поэтому внешняя ссылка передаёт экран параметром запроса, и
  // читаем мы его только тогда, когда в якоре маршрута нет (то есть на первом входе).
  const parseScreen=()=>{
    const route=location.hash.slice(1)
    if(Object.values(screens).includes(route))return route
    const wanted=new URLSearchParams(location.search).get('screen')
    return LINKABLE.includes(wanted)?wanted:screens.HOME
  }
  const [screen,setScreen]=useState(parseScreen)
  const [topic,setTopic]=useState(()=>localStorage.getItem('selectedTopic')||'Отношения')
  const [astroReading,setAstroReading]=useState(null)
  // Без дефолтного названия: если расклад ни разу не выбирали, Reading честно
  // попросит выбрать его из списка, а не откроет захардкоженный.
  const [readingTitle,setReadingTitle]=useState(()=>localStorage.getItem('selectedReading')||'')
  const [selectedSpread,setSelectedSpread]=useState(null)
  // Доступ с бэка (GET /users/me/access): что куплено и сколько осталось вопросов.
  // null — ещё не загрузился; в этом состоянии замки не рисуются, чтобы каталог не
  // мигал «закрыто → открыто». Единственный источник правды — сервер.
  const [access,setAccess]=useState(null)
  // Бесплатный тариф (GET /tariffs/trial): заведён ли он и забирал ли его этот человек.
  // null — ещё не спросили; до ответа всплывашка не показывается, чтобы она не мигала
  // перед тем, кто подарок уже забрал.
  const [trial,setTrial]=useState(null)
  // Закрытая «Позже» всплывашка. В состоянии, а не в localStorage: закрытие действует
  // до перезапуска мини-аппа, дальше предложение возвращается — пока не заберут.
  const [trialDismissed,setTrialDismissed]=useState(false)
  // Профиль с бэка: is_admin из БД, а не из initData — только он открывает админку.
  const [backendUser,setBackendUser]=useState(null)
  // Бан приходит с бэка (403 user_blocked) и может прилететь в ответ на любой запрос:
  // и на регистрацию при входе, и посреди сессии, если админ забанил прямо сейчас.
  const [blocked,setBlocked]=useState(false)
  const tg=window.Telegram?.WebApp
  useEffect(()=>{
    const onBlocked=()=>setBlocked(true)
    addEventListener(BLOCKED_EVENT,onBlocked)
    return()=>removeEventListener(BLOCKED_EVENT,onBlocked)
  },[])
  useEffect(()=>{
    let live=true
    tg?.ready();tg?.expand();tg?.setHeaderColor?.('#7044bc');tg?.setBackgroundColor?.('#7044bc');tg?.disableVerticalSwipes?.()
    // start_param — код приглашения (`ref_<id>`), с которым мини-аппу открыли по
    // реферальной ссылке. Бэк читает его же из подписанной initData, так что это
    // страховка, а не источник правды.
    registerUser(tg?.initDataUnsafe?.start_param||null).then(me=>{if(live)setBackendUser(me);return getAccess()}).then(data=>{if(live)setAccess(data)})
      .then(()=>getTrialTariff()).then(data=>{if(live)setTrial(data)})
      .catch(e=>{if(live&&isBlockedError(e))setBlocked(true)})
    // Заранее тянем каталоги в кэш, чтобы экраны открывались мгновенно.
    prefetchAstroCatalogue()
    prefetchTarotCatalogue()
    return()=>{live=false}
  },[])
  useEffect(()=>{const sync=()=>{const next=parseScreen();if(location.hash!==`#${next}`)history.replaceState(null,'',`#${next}`);setScreen(next)};sync();addEventListener('hashchange',sync);return()=>removeEventListener('hashchange',sync)},[])
  useEffect(()=>{if(location.hash!==`#${screen}`)location.hash=screen;window.scrollTo({top:0,behavior:'auto'});tg?.HapticFeedback?.impactOccurred?.('light')},[screen])
  const user=useMemo(()=>{const u=tg?.initDataUnsafe?.user;return {name:u?.first_name||'Гость',username:u?.username||'',photo:u?.photo_url||''}},[tg])
  const go=setScreen
  // Перечитать доступ после того, как он мог измениться: разбор списал штуку из
  // квоты, сообщение — вопрос из баланса. Тихо: неудачное обновление не должно
  // ломать экран, с которого его позвали.
  const refreshAccess=()=>{getAccess().then(setAccess).catch(()=>{})}
  // Перечитать состояние подарка после активации: по нему гаснет кнопка на витрине и
  // пропадает всплывашка. Тихо, как и доступ: экран из-за этого падать не должен.
  const refreshTrial=()=>{getTrialTariff().then(setTrial).catch(()=>{})}
  // Показываем только когда подарок есть и он не забран. Пока статус не пришёл
  // (trial === null), молчим.
  const trialOffered=!!trial?.available&&!trial.activated&&!trialDismissed
  const chooseTopic=next=>{setTopic(next);localStorage.setItem('selectedTopic',next);go(screens.LIST)}
  const pickAstroReading=item=>{setAstroReading(item);go(screens.BIRTH)}
  const openReading=(title,nextTopic,spread)=>{if(nextTopic){setTopic(nextTopic);localStorage.setItem('selectedTopic',nextTopic)}setSelectedSpread(spread||null);setReadingTitle(title);localStorage.setItem('selectedReading',title);go(screens.READING)}
  if(blocked)return <Blocked/>
  return <main className="app-shell">{screen===screens.HOME&&<Home go={go} chooseTopic={chooseTopic}/>} {screen===screens.CATEGORIES&&<Categories go={go} chooseTopic={chooseTopic}/>} {screen===screens.LIST&&<List go={go} topic={topic} openReading={openReading} access={access}/>} {screen===screens.READING&&<Reading go={go} topic={topic} title={readingTitle} spread={selectedSpread} onAccessChanged={refreshAccess}/>} {screen===screens.ASSISTANT&&<Assistant go={go} access={access} onAccessChanged={refreshAccess}/>} {screen===screens.BOT&&<BotWizard go={go}/>} {screen===screens.DREAM&&<DreamForm go={go}/>} {screen===screens.CHAT&&<Chat go={go} access={access} onAccessChanged={refreshAccess}/>} {screen===screens.ASTRO&&<AstroCatalogue go={go} onPick={pickAstroReading} access={access}/>} {screen===screens.BIRTH&&<Birth go={go} user={user} reading={astroReading} onAccessChanged={refreshAccess}/>} {screen===screens.FORMULA&&<Formula go={go}/>} {screen===screens.ARCHIVE&&<History go={go} onAccessChanged={refreshAccess}/>} {screen===screens.TARIFFS&&<TariffsScreen go={go} back={screens.HOME} onAccessChanged={refreshAccess} trial={trial} onTrialChanged={refreshTrial}/>} {screen===screens.PROFILE&&<Profile go={go} user={user} access={access} isAdmin={!!backendUser?.is_admin} onAccessChanged={refreshAccess}/>} {screen===screens.ADMIN&&(backendUser
    ?<Suspense fallback={<AdminBoot text="Загружаем админку…"/>}><AdminPanel user={backendUser} onExit={()=>go(screens.HOME)}/></Suspense>
    :<AdminBoot text="Проверяем доступ…"/>)} {trialOffered&&screen!==screens.TARIFFS&&screen!==screens.ADMIN&&<TrialOffer trial={trial} onClose={()=>setTrialDismissed(true)} onGo={()=>{setTrialDismissed(true);go(screens.TARIFFS)}}/>} <BottomNav screen={screen} go={go}/></main>
}

const rootElement=document.getElementById('root')
const appRoot=window.__TARO_APP_ROOT__||createRoot(rootElement)
window.__TARO_APP_ROOT__=appRoot
appRoot.render(<React.StrictMode><App/></React.StrictMode>)
